import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { getBasePathFromUrl } from '@/config/routes';
import { SOCIAL_URLS } from '@/config/socials';
import { OG_DEFAULT_IMAGE, OG_IMAGE_HEIGHT, OG_IMAGE_WIDTH, ROUTE_META, SITE_NAME, SITE_URL, ogImageFor, type RouteMeta } from '@/seo/route-meta';

/**
 * Head tags for public pages.
 *
 * Titles and descriptions come from the route manifest in src/seo/route-meta.ts,
 * the same file the Netlify edge layer serves to crawlers. Before this, React
 * read stale rows from the `seo_pages` table and overrode the server values, so
 * Googlebot saw one title in the HTML and a different one after hydration.
 * Props still win for dynamic pages (blog posts, share pages).
 */
interface SEOHeadProps {
  title?: string;
  description?: string;
  image?: string;
  type?: 'website' | 'article' | 'product';
  noIndex?: boolean;
  schema?: object;
  /** Meta for a page that is not in ROUTE_META (the links page on links.eyecaptain.io). */
  routeMeta?: RouteMeta;
  /** Deprecated no-ops kept so old call sites compile. */
  titleKey?: string;
  descriptionKey?: string;
  keywords?: string;
}

/** The brand share card (public/og/default.png); each public page has its own in public/og. */
const DEFAULT_IMAGE = OG_DEFAULT_IMAGE;

const ORGANIZATION = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': `${SITE_URL}/#organization`,
  name: SITE_NAME,
  alternateName: ['Eye Captain', 'eyecaptain.io'],
  legalName: 'Anvilfy Ltd',
  address: { '@type': 'PostalAddress', streetAddress: '71-75 Shelton Street', addressLocality: 'London', postalCode: 'WC2H 9JQ', addressCountry: 'GB' },
  url: SITE_URL,
  logo: `${SITE_URL}/logo.png`,
  image: DEFAULT_IMAGE,
  description: 'EyeCaptain is AI CRO audit software: it audits a web page on desktop and mobile and returns the changes that would convert more visitors, ranked by impact.',
  disambiguatingDescription: 'The conversion rate optimization software at eyecaptain.io, made by Anvilfy Ltd. Not related to the maritime camera system or the eye-tracking project of the same name.',
  foundingDate: '2024',
  founder: { '@type': 'Person', '@id': `${SITE_URL}/#author-dimitris`, name: 'Dimitris Andreadakis', url: `${SITE_URL}/authors/dimitris` },
  sameAs: SOCIAL_URLS,
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer service',
    email: 'hello@eyecaptain.io',
    availableLanguage: ['English'],
  },
};

const WEBSITE = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${SITE_URL}/#website`,
  name: SITE_NAME,
  alternateName: ['Eye Captain', 'eyecaptain.io'],
  url: `${SITE_URL}/`,
  publisher: { '@id': `${SITE_URL}/#organization` },
  potentialAction: {
    '@type': 'SearchAction',
    target: { '@type': 'EntryPoint', urlTemplate: `${SITE_URL}/blog?q={search_term_string}` },
    'query-input': 'required name=search_term_string',
  },
};

const SEGMENT_NAMES: Record<string, string> = {
  features: 'Features',
  solutions: 'Solutions',
  blog: 'Blog',
  authors: 'Authors',
};

const humanize = (seg: string) => seg.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export const SEOHead = ({
  title,
  description,
  image,
  type = 'website',
  noIndex = false,
  schema,
  routeMeta,
}: SEOHeadProps) => {
  const location = useLocation();
  const basePath = getBasePathFromUrl(location.pathname);
  const meta = routeMeta ?? ROUTE_META[basePath];
  const robotsNoIndex = noIndex || !!meta?.noindex;

  const finalTitle = title || meta?.title || `${SITE_NAME} | AI CRO Audit Platform`;
  const finalDescription = description || meta?.description || 'AI-powered CRO analysis platform';
  const finalImage = image || (meta && !routeMeta ? ogImageFor(basePath) : DEFAULT_IMAGE);
  /* Our own cards are 1200x630; a page's own picture (an article) has no known size. */
  const ownImage = finalImage.startsWith(`${SITE_URL}/og/`);
  const finalType = type === 'website' && meta?.type === 'article' ? 'article' : type;
  const canonicalUrl = meta?.canonical ?? `${SITE_URL}${basePath === '/' ? '/' : basePath}`;

  const pageSchema = schema ?? (meta?.schema ? { '@context': 'https://schema.org', ...meta.schema } : null);
  const faqSchema = meta?.faqs?.length
    ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        '@id': `${canonicalUrl}#faq`,
        mainEntity: meta.faqs.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      }
    : null;

  const breadcrumbSchema = basePath !== '/'
    ? (() => {
        const segments = basePath.split('/').filter(Boolean);
        const items = [{ '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` }];
        let acc = '';
        segments.forEach((seg, i) => {
          acc += `/${seg}`;
          const last = i === segments.length - 1;
          // /solutions, /features, /compare and /authors are not pages (they 404),
          // so they stay out of the trail instead of pointing crawlers at an error.
          if (!last && !ROUTE_META[acc]) return;
          const name = last
            ? (meta?.h1 || finalTitle.split('|')[0].split(':')[0].trim())
            : (SEGMENT_NAMES[seg] || ROUTE_META[acc]?.h1 || humanize(seg));
          items.push({ '@type': 'ListItem', position: items.length + 1, name, item: `${SITE_URL}${acc}` });
        });
        return { '@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement: items };
      })()
    : null;

  return (
    <Helmet>
      <html lang="en" />
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <meta name="author" content={SITE_NAME} />

      {robotsNoIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}

      <link rel="canonical" href={canonicalUrl} />
      <link rel="alternate" hrefLang="en" href={canonicalUrl} />
      <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />

      <meta property="og:type" content={finalType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:image" content={finalImage} />
      <meta property="og:image:alt" content={finalTitle} />
      {ownImage && <meta property="og:image:type" content="image/png" />}
      {ownImage && <meta property="og:image:width" content={String(OG_IMAGE_WIDTH)} />}
      {ownImage && <meta property="og:image:height" content={String(OG_IMAGE_HEIGHT)} />}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_US" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={finalImage} />
      <meta name="twitter:image:alt" content={finalTitle} />

      <script type="application/ld+json">{JSON.stringify(ORGANIZATION)}</script>
      <script type="application/ld+json">{JSON.stringify(WEBSITE)}</script>
      {breadcrumbSchema && <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>}
      {faqSchema && <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>}
      {pageSchema && <script type="application/ld+json">{JSON.stringify(pageSchema)}</script>}
    </Helmet>
  );
};

export default SEOHead;
